import React, { createContext, useState, useEffect, ReactNode, useContext } from "react";
import { User } from '@supabase/supabase-js';
import { auth } from "../services/supabase";
import PageLoader from "../components/PageLoader";

interface ProfileData {
  username: string;
  full_name?: string;
  gender?: string;
  tshirt_size?: string;
}

interface UserContextType {
  user: User | null;
  username: string;
  isPasswordRecovery: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, profileData: ProfileData) => Promise<{ alreadyRegistered: boolean; alreadyConfirmed: boolean }>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(() => window.location.pathname !== "/order-success");
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Get initial session
    auth.getUser().then(currentUser => {
      if (currentUser) {
        setUser(currentUser);
        setUsername(currentUser.user_metadata?.username || currentUser.email || "");
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      setUsername(currentUser?.user_metadata?.username || currentUser?.email || "");

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setIsPasswordRecovery(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await auth.signIn(email, password);
    if (error) throw error;
  };

  const signup = async (email: string, password: string, profileData: ProfileData) => {
    const { error, alreadyRegistered, alreadyConfirmed } = await auth.signUp(email, password, profileData);
    if (error) throw error;
    return { alreadyRegistered, alreadyConfirmed };
  };

  const logout = async () => {
    await auth.signOut();
    setUser(null);
    setUsername("");
    setIsPasswordRecovery(false);
  };

  const requestPasswordReset = async (email: string) => {
    await auth.requestPasswordReset(email);
  };

  const updatePassword = async (newPassword: string) => {
    await auth.updatePassword(newPassword);
    setIsPasswordRecovery(false);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await auth.changePassword(currentPassword, newPassword);
  };

  if (showLoader) {
    return (
      <PageLoader
        done={!loading}
        onComplete={() => setShowLoader(false)}
      />
    );
  }

  return (
    <UserContext.Provider value={{ user, username, isPasswordRecovery, login, signup, logout, requestPasswordReset, updatePassword, changePassword }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
};